/**
 * This chart centralizes fixed-coordinate Victory rendering so dashboard
 * features can compare arbitrary series without coupling visualization to domain fields.
 */
import React, { useMemo } from 'react';
import {
  Legend,
  LegendProvider,
  useVictoryTheme,
  victoryLinePlugin,
  victoryTooltipPlugin,
} from '@amzn/meridian';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { VictoryAxis, VictoryChart, VictoryLine, VictoryVoronoiContainer } from 'victory';
import { useTranslation } from 'src/arb-wrapper';
import { skeletonBase } from 'src/components/entity-metrics/skeleton';

const CHART_WIDTH = 800;
const CHART_HEIGHT = 320;
const MINIMUM_PLOT_HEIGHT = 260;
const LEFT_PADDING = 160;
const RIGHT_PADDING = 20;
const BOTTOM_PADDING = 72;
const SECONDARY_AXIS_OFFSET_X = 80;
const PRIMARY_AXIS_LABEL_PADDING = 58;
const SECONDARY_AXIS_LABEL_PADDING = 54;

const AXIS_LAYOUT = [
  {
    axis: 'primary',
    labelPadding: PRIMARY_AXIS_LABEL_PADDING,
    offsetX: LEFT_PADDING,
  },
  {
    axis: 'secondary',
    labelPadding: SECONDARY_AXIS_LABEL_PADDING,
    offsetX: SECONDARY_AXIS_OFFSET_X,
  },
] as const;

export interface ChartSeries {
  readonly key: string;
  readonly label: string;
  readonly color: string;
  readonly axis: 'primary' | 'secondary';
  readonly data: ReadonlyArray<{ x: string; y: number | null }>;
  readonly formatValue: (y: number) => string;
}

export interface MultiSeriesLineChartProps {
  readonly series: readonly ChartSeries[];
  readonly title: string;
  readonly headerAction?: React.ReactNode;
  readonly isLoading?: boolean;
  readonly minHeight?: number | string;
}

interface ChartDatum {
  readonly rawValue: number | null;
  readonly seriesKey: string;
  readonly x: string;
  readonly y: number | null;
}

const getAxisMaximum = (
  series: readonly ChartSeries[],
  axis: ChartSeries['axis'],
): number => Math.max(
  1,
  ...series
    .filter((candidate) => candidate.axis === axis)
    .flatMap(({ data }) => data.map(({ y }) => y ?? 0)),
);

const createDataset = (
  series: readonly ChartSeries[],
  primaryMaximum: number,
  secondaryMaximum: number,
) => series.map((item) => ({
  color: item.color,
  data: item.data.map(({ x, y }): ChartDatum => ({
    rawValue: y,
    seriesKey: item.key,
    x,
    y: y === null || item.axis === 'primary'
      ? y
      : y * primaryMaximum / secondaryMaximum,
  })),
  key: item.key,
  label: item.label,
}));

export const MultiSeriesLineChart: React.FC<MultiSeriesLineChartProps> = ({
  series,
  title,
  headerAction,
  isLoading,
  minHeight,
}) => {
  const { t } = useTranslation();
  const theme = useVictoryTheme({ showIndependentGrid: false });
  const { dataset, primaryMaximum, secondaryMaximum } = useMemo(() => {
    const nextPrimaryMaximum = getAxisMaximum(series, 'primary');
    const nextSecondaryMaximum = getAxisMaximum(series, 'secondary');
    return {
      dataset: createDataset(
        series,
        nextPrimaryMaximum,
        nextSecondaryMaximum,
      ),
      primaryMaximum: nextPrimaryMaximum,
      secondaryMaximum: nextSecondaryMaximum,
    };
  }, [series]);
  const axisLayout = AXIS_LAYOUT.map((layout) => ({
    ...layout,
    series: series.filter(({ axis }) => axis === layout.axis),
  })).sort((left, right) => left.offsetX - right.offsetX);
  const datasetByKey = new Map(dataset.map((item) => [item.key, item]));
  const legendDataset = axisLayout.flatMap(({ series: axisSeries }) =>
    axisSeries.map(({ key }) => datasetByKey.get(key)!),
  );
  const seriesByKey = new Map(series.map((item) => [item.key, item]));
  const hasData = series.some(({ data }) => data.length > 0);
  const formatDatum = (datum: ChartDatum): string | null => datum.rawValue === null
    ? null
    : seriesByKey.get(datum.seriesKey)?.formatValue(datum.rawValue) ??
      String(datum.rawValue);
  const denormalizeSecondaryTick = (tick: number): number =>
    tick / primaryMaximum * secondaryMaximum;

  return (
    <Column
      backgroundColor="white"
      data-testid="multi-series-line-chart"
      heights={['fit', 'fill']}
      minHeight={minHeight}
      minWidth={0}
      spacing="none"
      spacingInset="400"
      type="outline"
      width="100%"
    >
      <Row
        alignmentHorizontal="justify"
        alignmentVertical="center"
        spacing="300"
        wrap="down"
      >
        <Heading level={4}>{title}</Heading>
        {headerAction}
      </Row>
      {isLoading
        ? (
        <div
          data-testid="multi-series-line-chart-skeleton"
          style={{ ...skeletonBase, height: 260, marginTop: 16, width: '100%' }}
        />
          )
        : !hasData
            ? (
        <Column
          alignmentHorizontal="center"
          alignmentVertical="center"
          minHeight={200}
        >
          <Text color="secondary">{t('chart_no_data_available')}</Text>
        </Column>
              )
            : (
        <LegendProvider
          aria-label={title}
          data={legendDataset}
          plugins={[
            victoryLinePlugin({ theme }),
            victoryTooltipPlugin({ showCrosshair: true }),
          ]}
        >
          {({
            getLineProps,
            voronoiContainerProps,
            Tooltip,
          }: {
            getLineProps: (key: string) => Record<string, unknown>;
            voronoiContainerProps: Record<string, unknown>;
            Tooltip: React.ComponentType<{ legendKey: string }>;
          }) => (
            <Column
              height="100%"
              heights={['fill', 'fit']}
              spacing="none"
              width="100%"
            >
              <div
                data-testid="multi-series-line-chart-plot"
                style={{
                  minHeight: MINIMUM_PLOT_HEIGHT,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <VictoryChart
                  containerComponent={
                    <VictoryVoronoiContainer
                      {...voronoiContainerProps}
                      labels={({ datum }: { datum: ChartDatum }) =>
                        formatDatum(datum)!
                      }
                      responsive={true}
                      voronoiDimension="x"
                    />
                  }
                  domain={{ y: [0, primaryMaximum] }}
                  domainPadding={{ x: 16 }}
                  height={CHART_HEIGHT}
                  padding={{
                    bottom: BOTTOM_PADDING,
                    left: LEFT_PADDING,
                    right: RIGHT_PADDING,
                    top: 20,
                  }}
                  theme={theme}
                  width={CHART_WIDTH}
                >
                  <VictoryAxis
                    style={{
                      tickLabels: {
                        angle: -45,
                        fontSize: 10,
                        textAnchor: 'end',
                      },
                    }}
                    tickCount={Math.min(
                      Math.max(...series.map(({ data }) => data.length)),
                      7,
                    )}
                  />
                  {axisLayout.map(({
                    axis,
                    labelPadding,
                    offsetX,
                    series: axisSeries,
                  }) => axisSeries.length > 0 && (
                    <VictoryAxis
                      dependentAxis
                      key={axis}
                      label={axisSeries[0].label}
                      offsetX={offsetX}
                      orientation="left"
                      style={{
                        axisLabel: {
                          fontSize: 10,
                          padding: labelPadding,
                        },
                        ...(axis === 'secondary'
                          ? { grid: { stroke: 'transparent' } }
                          : {}),
                        tickLabels: { fontSize: 10 },
                      }}
                      tickFormat={axis === 'secondary'
                        ? (tick: number) => axisSeries[0].formatValue(
                            denormalizeSecondaryTick(tick),
                          )
                        : axisSeries[0].formatValue}
                    />
                  ))}
                  {dataset.map(({ data, key }) => (
                    <VictoryLine
                      data={data}
                      key={key}
                      labelComponent={<Tooltip legendKey={key} />}
                      labels={({ datum }: { datum: ChartDatum }) =>
                        formatDatum(datum)
                      }
                      x="x"
                      y="y"
                      {...getLineProps(key)}
                    />
                  ))}
                </VictoryChart>
              </div>
              <Column
                data-testid="multi-series-line-chart-legend-container"
                spacingInset="200 none 400 400"
              >
                <Legend direction="horizontal" />
              </Column>
            </Column>
          )}
        </LegendProvider>
              )}
    </Column>
  );
};
